import React, { useCallback, useMemo, useState } from "react";
import {
    Link,
    useLocation,
    useParams,
    useSearchParams,
} from "react-router-dom";

import { Chip } from "@nextui-org/react";
import { FaEye } from "react-icons/fa";
import { BiSolidCoupon } from "react-icons/bi";
import { MdDelete, MdEdit } from "react-icons/md";

import {
    formatDateTime,
    secondsToTime,
    convertURLParams,
    parseSearchParams,
    formatCurrency,
} from "@/utils/helpers";

import Modal from "@/components/Modal";
import ConfirmDelete from "@/components/ConfirmDelete";

import Table from "../../components/Table/Table";
import HeaderContent from "../../components/Table/components/HeaderContent";
import FooterContent from "../../components/Table/components/FooterContent";

import LectureView from "./LectureView";
import UpdateLecture from "./UpdateLecture";
import CreateLecture from "./CreateLecture";

import { useLectures } from "../hooks/useLectures";
import { useDeleteLecture } from "../hooks/useDeleteLecture";
import usePermission from "../../../../hooks/usePermission";

function LecturesTable() {
    const { search } = useLocation();
    const [searchValue, setSearchValue] = useState("");
    const [perPage, setPerPage] = useState(10);
    const [searchParams] = useSearchParams();

    const { can, canAll } = usePermission();
    const { courseId } = useParams();

    const { deleteLecture, isDeleting } = useDeleteLecture();
    //include
    const includeFields = canAll([
        "read:course:lecture:items",
        "read:course:lecture:materials",
    ])
        ? ["itemsCount", "materialsCount"]
        : can("read:course:lecture:items")
        ? ["itemsCount"]
        : can("read:course:lecture:materials")
        ? ["materialsCount"]
        : null;

    const include = [{ field: "VideosDuration", value: "true" }];

    const page = parseSearchParams(
        searchParams,
        "page",
        (value) => (parseInt(value, 10) < 1 ? 1 : parseInt(value, 10)),
        1
    );

    // filter , sort , page
    const filterAndSortAndPageQuery = convertURLParams(search);

    const { lectures, isPending } = useLectures({
        courseId,
        searchValue,
        include,
        includeFields,
        perPage,
        page,
        filterAndSortAndPageQuery,
    });

    //filterOptions
    const filterOptions = [
        { name: "مرئي", field: "visibility", value: "yes" },
        { name: "مخفي", field: "visibility", value: "no" },
    ];

    //headerRows
    const headers = canAll([
        "read:course:lecture:items",
        "read:course:lecture:materials",
    ])
        ? [
              { uid: "id", name: "#", sortable: true },
              { uid: "name", name: "اسم المحاضره", sortable: true },
              { uid: "courseId", name: "كود الكورس", sortable: true },
              { uid: "price", name: "السعر", sortable: true },
              { uid: "visibility", name: "الحاله", sortable: true },
              { uid: "createdAt", name: "أنشئت في", sortable: true },
              {
                  uid: "videosDuration",
                  name: "مدة الفيديوهات",
                  sortable: false,
              },
              {
                  uid: "itemsCount",
                  name: "عدد محتويات المحاضره",
                  sortable: false,
              },
              { uid: "materialsCount", name: "عدد الماتريال", sortable: false },
              { uid: "actions", name: "الافعال", sortable: false },
          ]
        : can("read:course:lecture:items")
        ? [
              { uid: "id", name: "#", sortable: true },
              { uid: "name", name: "اسم المحاضره", sortable: true },
              { uid: "courseId", name: "كود الكورس", sortable: true },
              { uid: "price", name: "السعر", sortable: true },
              { uid: "visibility", name: "الحاله", sortable: true },
              { uid: "createdAt", name: "أنشئت في", sortable: true },
              {
                  uid: "videosDuration",
                  name: "مدة الفيديوهات",
                  sortable: false,
              },
              {
                  uid: "itemsCount",
                  name: "عدد محتويات المحاضره",
                  sortable: false,
              },
              { uid: "actions", name: "الافعال", sortable: false },
          ]
        : can("read:course:lecture:materials")
        ? [
              { uid: "id", name: "#", sortable: true },
              { uid: "name", name: "اسم المحاضره", sortable: true },
              { uid: "courseId", name: "كود الكورس", sortable: true },
              { uid: "price", name: "السعر", sortable: true },
              { uid: "visibility", name: "الحاله", sortable: true },
              { uid: "createdAt", name: "أنشئت في", sortable: true },
              {
                  uid: "videosDuration",
                  name: "مدة الفيديوهات",
                  sortable: false,
              },
              { uid: "materialsCount", name: "عدد الماتريال", sortable: false },
              { uid: "actions", name: "الافعال", sortable: false },
          ]
        : [
              { uid: "id", name: "#", sortable: true },
              { uid: "name", name: "اسم المحاضره", sortable: true },
              { uid: "courseId", name: "كود الكورس", sortable: true },
              { uid: "price", name: "السعر", sortable: true },
              { uid: "visibility", name: "الحاله", sortable: true },
              { uid: "createdAt", name: "أنشئت في", sortable: true },
              {
                  uid: "videosDuration",
                  name: "مدة الفيديوهات",
                  sortable: false,
              },
              { uid: "actions", name: "الافعال", sortable: false },
          ];

    //defaultHeaders
    const INITIAL_VISIBLE_COLUMNS = [
        "id",
        "name",
        "price",
        "visibility",
        "videosDuration",
        "itemsCount",
        "materialsCount",
        "actions",
    ];

    //Actions
    const actions = useMemo(
        () => [
            {
                id: "rowDetails",
                name: "رؤية",
                icon: <FaEye className="text-lg" />,
                content: (row) => <LectureView lecture={row} />,
                permissions: ["read:course:lectures"],
            },
            {
                id: "edit",
                name: "تعديل",
                icon: <MdEdit className="text-lg text-blue-color-primary" />,
                content: (row) => <UpdateLecture lecture={row} />,
                permissions: ["update:course:lectures"],
            },
            {
                id: "delete",
                name: "حذف",
                icon: <MdDelete className="text-lg text-red-color-primary" />,
                permissions: ["delete:course:lectures"],
            },
        ],
        []
    );

    //addRow
    const addRow = {
        row: <CreateLecture />,
        permission: can("create:course:lectures"),
    };
    //formatting Data
    const reformattedData = lectures?.data?.map((item) => {
        const { id, attributes, relationships, videosDuration, ...rest } = item;

        const lecturesData = {
            id,
            ...attributes,
            courseId: attributes?.courseId,
            ...relationships,
            videosDuration: secondsToTime(videosDuration),
        };

        return {
            ...lecturesData,
            ...rest,
        };
    });

    const renderCell = useCallback(
        (row, columnKey) => {
            const cellValue = row?.[columnKey];
            switch (columnKey) {
                case "visibility":
                    return (
                        <Chip
                            className="gap-1 capitalize border-none text-default-600"
                            color={row.visibility ? "success" : "warning"}
                            size="sm"
                            variant="dot"
                        >
                            {cellValue == 1 ? "مفعل" : "غير مفعل"}
                        </Chip>
                    );
                case "price":
                    return cellValue !== 0 ? (
                        <span className="text-green-700">
                            {formatCurrency(cellValue)}
                        </span>
                    ) : (
                        <span className="text-green-700">ببلاش</span>
                    );
                case "itemsCount":
                    return cellValue === 0 ? (
                        <span className="text-green-700">&mdash;</span>
                    ) : (
                        <span>{cellValue}</span>
                    );
                case "materialsCount":
                    return cellValue === 0 ? (
                        <span className="text-green-700">&mdash;</span>
                    ) : (
                        <span>{cellValue}</span>
                    );

                case "createdAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "updatedAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "actions":
                    return (
                        <div className="relative flex items-center justify-start gap-2">
                            {can("create:course:lecture:vouchers") && (
                                <Link
                                    to={`/admin/courses/${courseId}/lectures/${row?.id}/vouchers`}
                                >
                                    <BiSolidCoupon className="text-lg text-blue-color-primary" />
                                </Link>
                            )}
                            <Modal>
                                {actions?.map(
                                    (action) =>
                                        canAll(action.permissions) && (
                                            <>
                                                <React.Fragment key={action.id}>
                                                    <Modal.Open
                                                        opens={action.id}
                                                    >
                                                        <button>
                                                            {action.icon}
                                                        </button>
                                                    </Modal.Open>
                                                    <Modal.Window
                                                        name={action.id}
                                                    >
                                                        {action.id ===
                                                        "rowDetails" ? (
                                                            action.content(row)
                                                        ) : action.id ===
                                                          "edit" ? (
                                                            action.content(row)
                                                        ) : action.id ===
                                                          "delete" ? (
                                                            <ConfirmDelete
                                                                resourceName={
                                                                    row?.name
                                                                }
                                                                disabled={
                                                                    isDeleting
                                                                }
                                                                onConfirm={() =>
                                                                    deleteLecture(
                                                                        {
                                                                            courseId,
                                                                            lectureId:
                                                                                row?.id,
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        ) : null}
                                                    </Modal.Window>
                                                </React.Fragment>
                                            </>
                                        )
                                )}
                            </Modal>
                        </div>
                    );
                default:
                    return cellValue;
            }
        },
        [actions, deleteLecture, canAll, can, isDeleting, courseId]
    );

    const [visibleColumns, setVisibleColumns] = useState(
        new Set(INITIAL_VISIBLE_COLUMNS)
    );

    const totalPages = Math.ceil(
        lectures?.meta?.total / lectures?.meta?.per_page
    );

    return (
        <>
            <Table
                isloading={isPending}
                rows={reformattedData}
                headers={headers}
                visibleColumns={visibleColumns}
                renderCell={renderCell}
                columnsToCopy={["name"]}
                topContent={
                    <HeaderContent
                        filterOptions={filterOptions}
                        visibleColumns={visibleColumns}
                        setVisibleColumns={setVisibleColumns}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        placeholder="يمكنك البحث باسم المحاضره او الوصف او ال ID"
                        headers={headers}
                        rowsNumber={perPage}
                        setRowsNumber={setPerPage}
                        addRow={addRow}
                        totalRows={lectures?.meta?.total}
                        modalWidth="w-full md:w-[90%] lg:w-1/2"
                    />
                }
                bottomContent={<FooterContent totalPages={totalPages} />}
            />
        </>
    );
}
export default LecturesTable;
